import {
    Alert,
    AlertIcon,
    Avatar,
    Badge,
    Box,
    Button, // Added Container for responsive padding
    Card, // Using Card for better visual separation of items
    CardBody,
    Container,
    Flex,
    Heading,
    HStack,
    Icon,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select, // Not used in this component, consider removing if truly not needed
    SimpleGrid,
    Spinner,
    Text,
    useDisclosure,
    VStack
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { getAllPickups } from '../../api';

import dayjs from 'dayjs';
import {
    FaChevronLeft, // Icon for collapsing sidebar
    FaChevronRight // Icon for expanding sidebar
    ,








    FaClock,
    FaMapMarkerAlt,
    FaStickyNote,
    FaTasks
} from 'react-icons/fa';
import PickupNotes from '../Pickups/PickupNotes'; // Ensure this component's layout is also mobile-friendly if it's rendered

const formatDate = (val) => dayjs(val).format("MMM D, h:mm A");

export default function PickupMap() {
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedPickup, setSelectedPickup] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [filters, setFilters] = useState({ Default: true, DropOffOnly: false, Supplies: false });
    const [couriers, setCouriers] = useState([]);
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [courierPickups, setCourierPickups] = useState([]);
    const [showSidebar, setShowSidebar] = useState(true); // Default to true, users can toggle
    const mapRef = useRef(null);

    useEffect(() => {
        if (!window.mapkit || !window.mapkit.Map) return;

        window.mapkit.init({
            authorizationCallback: done => done(process.env.REACT_APP_MAP_TOKEN),
        });

        mapRef.current = new window.mapkit.Map('map-container', {
            showsPointsOfInterest: true,
            showsBuildings: true, // Enables 3D buildings when zoomed in
            isRotationEnabled: true,
            isZoomEnabled: true,
            isScrollEnabled: true,
            isPitchEnabled: true, // Required for 3D view
            mapType: window.mapkit.Map.MapTypes.Standard // Options: Standard, Hybrid, Satellite
        });

        mapRef.current.region = new window.mapkit.CoordinateRegion(
            new window.mapkit.Coordinate(34.0522, -118.2437),
            new window.mapkit.CoordinateSpan(1.5, 1.5)
        );

        getPickups(mapRef.current);
    }, []);


    useEffect(() => {
        if (window.mapkit && mapRef.current) {
            mapRef.current.removeAnnotations(mapRef.current.annotations);
            getPickups(mapRef.current);
        }
    }, [filters, selectedCourier]);

    const formatAddress = (address) => {
        if (!address) return '';
        const parts = [address.address1, address.address2, address.city, address.state, address.zip].filter(Boolean);
        return parts.join(', ');
    };

    const getPickups = async (mapInstance) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllPickups(1, 9999);
            if (response.success) {
                const allPickups = response.data.pickups.filter(p => p.status === 'PENDING');

                const courierList = [...new Map(allPickups.map(p => [p.courier?._id, p.courier])).values()]
                    .filter(Boolean)
                    .map(c => ({ id: c._id, name: c.name }));
                setCouriers(courierList);

                const filtered = allPickups.filter(p =>
                    filters[p.type] &&
                    (!selectedCourier || p.courier?._id === selectedCourier)
                );
                setCourierPickups(filtered);

                const geocoder = new window.mapkit.Geocoder();
                // Clear existing annotations before adding new ones
                mapInstance.removeAnnotations(mapInstance.annotations);
                filtered.forEach(pickup => {
                    const location = pickup.type === 'Default' ? pickup.pickupLocation : pickup.dropOffLocation;
                    if (location?.address) {
                        const fullAddress = formatAddress(location.address);
                        geocoder.lookup(fullAddress, (err, data) => {
                            if (!err && data?.results?.length) {
                                const { coordinate } = data.results[0];
                                const annotation = new window.mapkit.MarkerAnnotation(coordinate, {
                                    title: pickup.type === 'Default' ? "Pickup" : pickup.type === 'Supplies' ? "Supplies" : "Drop Off",
                                    subtitle: location.name || fullAddress,
                                    calloutEnabled: true,
                                    glyphImage: {
                                        url: "./blue-dot.svg",   // <-- path to your blue-dot.svg
                                        accessibilityLabel: "Pickup Location"
                                    },
                                    // clusteringIdentifier: "pickupCluster" // Uncomment if you want clustering
                                });
                                annotation.addEventListener('select', () => {
                                    setSelectedPickup(pickup);
                                    onOpen();
                                });
                                mapInstance.addAnnotation(annotation);
                            }
                        });
                    }
                });
            } else {
                setError(response.error);
            }
        } catch (error) {
            setError('Failed to load pickups');
        }
        setLoading(false);
    };

    return (
        <Container maxW="container.xl">
            <Heading size="lg" mb={6}>Pending Pickups Map</Heading>

            {loading && <Spinner size="xl" thickness='4px' color='blue.500' />}
            {error && <Alert status='error' my={4}><AlertIcon />{error}</Alert>}

            {/* Filter and Courier Selection Section */}
            <Box mb={6} p={4} borderWidth="1px" borderRadius="lg" bg="gray.50">
                <HStack spacing={{ base: 2, md: 4 }} wrap="wrap">
                    <Text fontWeight="semibold" mr={2}>Filter by Type:</Text>
                    {Object.keys(filters).map(type => (
                        <Button
                            key={type}
                            size="sm"
                            variant={filters[type] ? "solid" : "outline"}
                            colorScheme="blue"
                            onClick={() => setFilters(prev => ({ ...prev, [type]: !prev[type] }))}
                        >
                            {type}
                        </Button>
                    ))}
                    <Select
                        placeholder="Filter by Courier"
                        value={selectedCourier || ''}
                        onChange={(e) => setSelectedCourier(e.target.value)}
                        width={{ base: '100%', md: '200px', lg: '250px' }} // Make width responsive
                        ml={{ base: 0, md: 4 }} // Add margin on medium and larger screens
                    >
                        {couriers.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </Select>
                </HStack>
            </Box>

            {/* Main Content: Map and Sidebar */}
            <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
                {/* Map Container */}
                <Box
                    id="map-container"
                    borderRadius="lg"
                    overflow="hidden"
                    border="1px solid #ddd"
                    h={900} // Adjusted height for mobile
                    flex={showSidebar && selectedCourier ? 2 : 1} // Map takes more space when sidebar is open
                    minW={800} // Ensure map doesn't shrink too much
                />

                {/* Sidebar Toggle Button for smaller screens (optional, could be in header too) */}
                {selectedCourier && (
                    <Box display={{ base: 'block', md: 'none' }} mt={2}>
                        <Button
                            onClick={() => setShowSidebar(prev => !prev)}
                            size="sm"
                            width="100%"
                            leftIcon={showSidebar ? <FaChevronLeft /> : <FaChevronRight />}
                        >
                            {showSidebar ? 'Hide Courier Pickups' : 'Show Courier Pickups'}
                        </Button>
                    </Box>
                )}

                {/* Courier Pickups Sidebar */}
                {selectedCourier && (
                    <Box
                        w={{ base: '100%', md: '35%' }} // Responsive width
                        maxH={{ base: '300px', md: '600px' }} // Max height for scrolling
                        overflowY="auto"
                        borderWidth="1px"
                        borderRadius="lg"
                        p={4}
                        bg="white"
                        boxShadow="sm"
                    >
                        <Heading size="md" mb={4}>Courier's Pickups</Heading>
                        <VStack spacing={3} align="stretch">
                            {courierPickups.length > 0 ? (
                                courierPickups
                                    .sort((a, b) => Number(a.pickupOrder) - Number(b.pickupOrder))
                                    .map(p => (
                                        <Card
                                            key={p._id}
                                            p={3}
                                            cursor="pointer"
                                            _hover={{ bg: 'gray.50', boxShadow: 'md' }}
                                            onClick={() => {
                                                setSelectedPickup(p);
                                                onOpen();
                                            }}
                                            size="sm" // Make card size smaller
                                        >
                                            <CardBody p={2}>
                                                <Text fontWeight="bold" noOfLines={1}>{p.pickupLocation?.name || p.dropOffLocation?.name}</Text>
                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                    {formatAddress(p.pickupLocation?.address || p.dropOffLocation?.address)}
                                                </Text>
                                                <HStack mt={1} spacing={2} justifyContent="space-between">
                                                    <Badge colorScheme="purple" fontSize="xs">{p.type}</Badge>
                                                    {p.courier && <Badge colorScheme="blue" fontSize="xs">{p.courier.name}</Badge>}
                                                    <Text fontSize="xs" color="gray.500">{formatDate(p.createdAt)}</Text>
                                                </HStack>
                                            </CardBody>
                                        </Card>
                                    ))
                            ) : (
                                <Text color="gray.500">No pending pickups for this courier.</Text>
                            )}
                        </VStack>
                    </Box>
                )}
            </Flex>

            {/* Modal for Selected Pickup Details */}
            {isOpen && selectedPickup && (
                <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader pb={2}>
                            <HStack spacing={4} align="center">
                                <Avatar size="md" name={selectedPickup?.courier?.name} />
                                <VStack align="start" spacing={0}>
                                    <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" noOfLines={1}>
                                        {selectedPickup?.pickupLocation?.name || selectedPickup?.dropOffLocation?.name}
                                    </Text>
                                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" noOfLines={1}>
                                        {formatAddress(selectedPickup?.pickupLocation?.address || selectedPickup?.dropOffLocation?.address)}
                                    </Text>
                                    <Badge colorScheme="blue" mt={1}>{selectedPickup?.status}</Badge>
                                </VStack>
                            </HStack>
                        </ModalHeader>
                        <ModalBody pt={2}>
                            <VStack spacing={8} align="stretch">
                                {/* Pickup Overview */}
                                <Box p={6} borderWidth="1px" borderRadius="lg" bg="white" boxShadow="sm">
                                    <Heading size="md" mb={4}><Icon as={FaMapMarkerAlt} mr={2} />Pickup Overview</Heading>
                                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                                        <Text><b>Pickup Location:</b> {selectedPickup?.pickupLocation?.name}</Text>
                                        <Text><b>Drop-off Location:</b> {selectedPickup?.dropOffLocation?.name}</Text>
                                        <Text><b>Courier:</b> {selectedPickup?.courier?.name || 'N/A'}</Text>
                                        <Text><b>Type:</b> {selectedPickup?.type}</Text>
                                        <Text><b>Route:</b> {selectedPickup?.routeId?.name || 'On Call'}</Text>
                                        <Text><b>Order #:</b> {selectedPickup?.pickupOrder || 'N/A'}</Text>
                                    </SimpleGrid>
                                </Box>

                                {/* Pickup Requirements */}
                                <Box p={6} borderWidth="1px" borderRadius="lg" bg="white" boxShadow="sm">
                                    <Heading size="md" mb={4}><Icon as={FaTasks} mr={2} />Pickup Requirements</Heading>
                                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                                        <Text><b>Request Photo:</b> <Badge colorScheme={selectedPickup?.requirements?.requestPhoto ? 'green' : 'gray'}>{selectedPickup?.requirements?.requestPhoto ? 'Yes' : 'No'}</Badge></Text>
                                        <Text><b>Has Supplies:</b> <Badge colorScheme={selectedPickup?.requirements?.hasSupplies ? 'green' : 'gray'}>{selectedPickup?.requirements?.hasSupplies ? 'Yes' : 'No'}</Badge></Text>
                                        <Text><b>Has Docs:</b> <Badge colorScheme={selectedPickup?.requirements?.hasDocs ? 'green' : 'gray'}>{selectedPickup?.requirements?.hasDocs ? 'Yes' : 'No'}</Badge></Text>
                                    </SimpleGrid>
                                </Box>

                                {/* Details */}
                                <Box p={6} borderWidth="1px" borderRadius="lg" bg="white" boxShadow="sm">
                                    <Heading size="md" mb={4}><Icon as={FaClock} mr={2} />Details</Heading>
                                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                                        <Text><b>Created:</b> {formatDate(selectedPickup?.createdAt)}</Text>
                                        <Text><b>Route Pickup:</b> <Badge colorScheme={selectedPickup?.isRoutePickup ? 'green' : 'red'}>{selectedPickup?.isRoutePickup ? 'Yes' : 'No'}</Badge></Text>
                                    </SimpleGrid>
                                </Box>

                                {/* PickupNotes component (ensure it's mobile-friendly internally) */}
                                {selectedPickup?._id && (
                                    <Box p={6} borderWidth="1px" borderRadius="lg" bg="white" boxShadow="sm">
                                        <Heading size="md" mb={4}><Icon as={FaStickyNote} mr={2} />Pickup Notes</Heading>
                                        <PickupNotes pickupId={selectedPickup._id} />
                                    </Box>
                                )}
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={onClose} colorScheme="gray">Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </Container>
    );
}